import { PageHead } from '@/ui';
import { ArrowRight, Plus } from '@/ui/icons';
import PropTypes from 'prop-types';
import ActuacionesFeed from './ActuacionesFeed';
import Agenda from './Agenda';
import './DashboardView.css';
import FocusCard from './FocusCard';
import { Kpi, KpiHero } from './Kpi';

// Inicio — the operator deck. Presentational: the page (Dashboard.jsx) derives
// every number from real data and hands down a view-model + navigation
// callbacks. Nothing here is a literal.
export default function DashboardView({
  hoy = '',
  firstName = '',
  requierenAccion = 0,
  focus = [],
  metrics = {},
  agendaDays = [],
  feedState = 'ready',
  feedRows = [],
  dashboardAvailable = true,
  onNuevaDiligencia,
  onSuscribirCausa,
  onGo,
}) {
  const go = (route) => () => onGo && onGo(route);

  const sub = (
    <>
      <b>
        {requierenAccion}{' '}
        {requierenAccion === 1
          ? 'diligencia requiere'
          : 'diligencias requieren'}
      </b>{' '}
      tu atención hoy.
    </>
  );

  const actions = (
    <>
      <button
        type="button"
        className="t-btn t-btn--quiet"
        onClick={onNuevaDiligencia}
      >
        <Plus size={15} />
        Nueva diligencia
        <span className="kbd">D</span>
      </button>
      <button
        type="button"
        className="t-btn t-btn--primary"
        onClick={onSuscribirCausa}
      >
        <Plus size={15} />
        Suscribir causa
        <span className="kbd">N</span>
      </button>
    </>
  );

  return (
    <div className="page page--wide reveal">
      <PageHead
        kicker={`Panel operativo · ${hoy}`}
        title={`Hola, ${firstName}.`}
        sub={sub}
        action={actions}
      />

      <section className="focus">
        <div className="focus__head">
          <div className="kicker">Requiere tu atención hoy</div>
          <div className="focus__count mono">
            {String(focus.length).padStart(2, '0')}
          </div>
        </div>
        {focus.length === 0 ? (
          <div className="note" style={{ padding: '6px 2px' }}>
            Nada urgente. Todas las diligencias están en curso o cerradas.
          </div>
        ) : (
          <div className="focus__grid">
            {focus.map((f) => (
              <FocusCard
                key={f.key}
                tone={f.tone}
                tagLabel={f.tagLabel}
                idLabel={f.idLabel}
                what={f.what}
                caratula={f.caratula}
                ctaLabel={f.ctaLabel}
                onClick={f.onClick}
              />
            ))}
          </div>
        )}
      </section>

      <section className="kpi-strip">
        <KpiHero
          label="Diligencias activas"
          value={metrics.diligenciasActivas ?? 0}
          meta={
            <>
              {`// ${metrics.requierenAccion ?? 0} requieren`}
              <br />
              acción
            </>
          }
          delta={`${metrics.cotizando ?? 0} cotizando · ${metrics.enCurso ?? 0} en curso`}
          hint="Ver diligencias"
          onClick={go('/diligencias')}
        />
        <Kpi
          label="Requieren acción"
          value={metrics.requierenAccion ?? 0}
          hint="Ver diligencias"
          onClick={go('/diligencias?attn=1')}
        />
        <Kpi
          label="Cotizando"
          value={metrics.cotizando ?? 0}
          hint="Ver"
          onClick={go('/diligencias?stage=cotizando')}
        />
        <Kpi
          label="Causas activas"
          value={
            dashboardAvailable ? (
              (metrics.causasActivas ?? 0)
            ) : (
              <span role="img" aria-label="Dato no disponible para tu rol">
                —
              </span>
            )
          }
          hint="Ver causas"
          onClick={go('/causas?tab=activas')}
        />
        <Kpi
          label="Listo para pagar"
          value={metrics.listo ?? 0}
          hint="Ir a pagos"
          onClick={go('/pagos')}
        />
      </section>

      <section
        className={`dash-cols${dashboardAvailable ? '' : ' dash-cols--single'}`}
      >
        <div className="dash-card">
          <div className="dash-card__head">
            <div className="dash-card__title">Próximos 5 días hábiles</div>
            <button
              type="button"
              className="dash-card__link"
              onClick={go('/diligencias')}
            >
              Ver diligencias <ArrowRight size={13} />
            </button>
          </div>
          <Agenda days={agendaDays} />
        </div>

        {dashboardAvailable && (
          <div className="dash-card">
            <div className="dash-card__head">
              <div className="dash-card__title">Actuaciones recientes</div>
              <span className="note">Recibidas del PJUD</span>
            </div>
            <ActuacionesFeed state={feedState} rows={feedRows} />
          </div>
        )}
      </section>
    </div>
  );
}

DashboardView.propTypes = {
  hoy: PropTypes.string,
  firstName: PropTypes.string,
  requierenAccion: PropTypes.number,
  focus: PropTypes.array,
  metrics: PropTypes.object,
  agendaDays: PropTypes.array,
  feedState: PropTypes.oneOf(['loading', 'error', 'empty', 'ready']),
  feedRows: PropTypes.array,
  dashboardAvailable: PropTypes.bool,
  onNuevaDiligencia: PropTypes.func,
  onSuscribirCausa: PropTypes.func,
  onGo: PropTypes.func,
};
